import { useCallback, useEffect, useState } from 'react'
import { useSession } from '@/context/AuthContext'
import {
  Users2, UserPlus, Trash2, ShieldCheck, User as UserIcon,
  Loader2, Building2, Mail, CheckCircle2, AlertTriangle, Eye,
} from 'lucide-react'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { STAFF_ROLE, roleLevel, rolesForDepartment } from '@/lib/roles'
import { DEPARTMENTS } from '@/lib/departments'
import { formatDate } from '@/lib/utils'

interface AdminUser {
  email: string
  username: string
  role: string
  department: string | null
  isAdmin: boolean
  canSetVisibility: boolean
  lastLoginAt: string
}

export default function UserManagementPage() {
  const { data: session } = useSession()
  const myEmail = (session?.user?.email ?? '').toLowerCase()

  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [busyEmail, setBusyEmail] = useState<string | null>(null)
  // Konfirmasi dipegang di dalam aplikasi, bukan window.confirm. Lihat ConfirmDialog
  // untuk alasannya: browser boleh menolak menampilkan dialog bawaan, dan waktu itu
  // terjadi setiap aksi di halaman ini mati tanpa suara.
  const [konfirmasi, setKonfirmasi] = useState<
    { title: string; message: string; confirmLabel: string; danger?: boolean; run: () => void } | null
  >(null)

  // Invite form
  const [iName, setIName] = useState('')
  const [iEmail, setIEmail] = useState('')
  const [iRole, setIRole] = useState<string>(STAFF_ROLE)
  const [iDept, setIDept] = useState<string>(DEPARTMENTS[1] ?? DEPARTMENTS[0])
  const [iAdmin, setIAdmin] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/admin/users')
      if (res.status === 403) {
        setForbidden(true)
        return
      }
      const data = await res.json()
      setUsers(data.users ?? [])
    } catch {
      setFeedback({ kind: 'err', msg: 'Gagal memuat daftar user' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    if (inviting) return
    setInviting(true)
    setFeedback(null)
    try {
      const res = await fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: iName, email: iEmail, role: iRole, department: iDept, isAdmin: iAdmin }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error ?? 'Gagal meng-invite user')
      setFeedback({
        kind: 'ok',
        msg: data?.invitationEmailQueued === false
          ? `${iEmail} berhasil di-invite ke ${iDept}, tapi email undangannya gagal dikirim`
          : `${iEmail} berhasil di-invite ke ${iDept} dan email undangannya dikirim`,
      })
      setIName(''); setIEmail(''); setIRole(STAFF_ROLE); setIAdmin(false)
      await load()
    } catch (err) {
      setFeedback({ kind: 'err', msg: err instanceof Error ? err.message : 'Terjadi kesalahan' })
    } finally {
      setInviting(false)
    }
  }

  // PATCH satu field user (role / department / isAdmin / canSetVisibility).
  async function patchUser(email: string, patch: Partial<Pick<AdminUser, 'role' | 'department' | 'isAdmin' | 'canSetVisibility'>>) {
    setBusyEmail(email)
    setFeedback(null)
    try {
      const res = await fetch(`/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error ?? 'Gagal memperbarui user')
      setUsers((prev) => prev.map((u) => (u.email === email ? { ...u, ...patch } : u)))
    } catch (err) {
      setFeedback({ kind: 'err', msg: err instanceof Error ? err.message : 'Terjadi kesalahan' })
      await load() // sinkron ulang kalau gagal
    } finally {
      setBusyEmail(null)
    }
  }

  function askRemoveUser(email: string) {
    setKonfirmasi({
      title: 'Hapus user',
      message: `${email} akan dihapus dari daftar user. Tindakan ini tidak bisa dibatalkan.`,
      confirmLabel: 'Hapus',
      danger: true,
      run: () => { void removeUser(email) },
    })
  }

  async function removeUser(email: string) {
    setBusyEmail(email)
    setFeedback(null)
    try {
      const res = await fetch(`/api/admin/users/${encodeURIComponent(email)}`, { method: 'DELETE' })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error ?? 'Gagal menghapus user')
      setUsers((prev) => prev.filter((u) => u.email !== email))
      setFeedback({ kind: 'ok', msg: `${email} dihapus` })
    } catch (err) {
      setFeedback({ kind: 'err', msg: err instanceof Error ? err.message : 'Terjadi kesalahan' })
    } finally {
      setBusyEmail(null)
    }
  }

  // ── Akses ditolak ────────────────────────────────────────────────────────
  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#F5F4F0] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E8E7E3] rounded-2xl p-8 max-w-sm text-center">
          <ShieldCheck className="w-8 h-8 text-[#C0BDB6] mx-auto mb-3" />
          <h1 className="font-display text-lg font-bold text-[#1A1916]">Akses Ditolak</h1>
          <p className="text-sm text-[#8A8A85] font-body mt-2">
            Halaman ini hanya untuk pemegang <span className="font-semibold">flag Admin</span>.
          </p>
        </div>
      </div>
    )
  }

  const inputCls = 'w-full min-h-[44px] px-3 py-2.5 bg-white border border-[#E0DFD8] rounded-xl text-sm font-body text-[#1A1916] focus:outline-none focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 disabled:opacity-50 transition-all'

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-30">
        <div className="px-6 flex items-center gap-3" style={{ height: '52px' }}>
          <Users2 className="w-4 h-4 text-[#6366F1]" />
          <span className="font-display text-[15px] font-bold text-[#1A1916]">Manajemen User</span>
          <span className="text-[10px] uppercase tracking-wider font-body font-semibold text-[#6366F1] bg-[#EEF2FF] border border-[#E0E7FF] px-2 py-0.5 rounded-md">
            Admin · semua unit bisnis
          </span>
        </div>
      </header>

      <main className="px-6 py-6 max-w-6xl mx-auto space-y-5">
        {/* Feedback banner */}
        {feedback && (
          <div className={`flex items-start gap-2.5 px-3.5 py-3 rounded-xl border text-xs font-body ${
            feedback.kind === 'ok'
              ? 'bg-[#F0FDF4] border-[#BBF7D0] text-[#15803D]'
              : 'bg-[#FEF2F2] border-[#FECACA] text-[#DC2626]'
          }`}>
            {feedback.kind === 'ok'
              ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              : <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
            <span className="leading-relaxed">{feedback.msg}</span>
          </div>
        )}

        {/* ── Invite ───────────────────────────────────────────────────────── */}
        <section className="bg-white border border-[#E8E7E3] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-4 h-4 text-[#6B6860]" />
            <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold">
              Invite Anggota
            </h2>
          </div>

          <form onSubmit={handleInvite} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
            <div className="lg:col-span-3">
              <label htmlFor="i-name" className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">Nama</label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#AEADA8] pointer-events-none" />
                <input id="i-name" value={iName} onChange={(e) => setIName(e.target.value)} required disabled={inviting}
                  placeholder="Nama lengkap" className={`${inputCls} pl-9`} />
              </div>
            </div>
            <div className="lg:col-span-3">
              <label htmlFor="i-email" className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#AEADA8] pointer-events-none" />
                <input id="i-email" type="email" value={iEmail} onChange={(e) => setIEmail(e.target.value)} required disabled={inviting}
                  placeholder="nama@domain.com" className={`${inputCls} pl-9`} />
              </div>
            </div>
            <div className="lg:col-span-2">
              <label htmlFor="i-dept" className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">Departemen</label>
              <select id="i-dept" value={iDept}
                onChange={(e) => {
                  const dept = e.target.value
                  setIDept(dept)
                  // Director hanya sah di HOLDING — reset pilihan role yang jadi tak valid.
                  if (!(rolesForDepartment(dept) as readonly string[]).includes(iRole)) setIRole(STAFF_ROLE)
                }}
                disabled={inviting}
                className={`${inputCls} cursor-pointer`}>
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="lg:col-span-2">
              <label htmlFor="i-role" className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">Role</label>
              <select id="i-role" value={iRole} onChange={(e) => setIRole(e.target.value)} disabled={inviting}
                className={`${inputCls} cursor-pointer`}>
                {rolesForDepartment(iDept).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="lg:col-span-2 flex flex-col gap-2">
              <label className="flex items-center gap-2 min-h-[24px] text-[12px] font-body text-[#6B6860] cursor-pointer select-none">
                <input type="checkbox" checked={iAdmin} onChange={(e) => setIAdmin(e.target.checked)} disabled={inviting}
                  className="w-4 h-4 rounded border-[#E0DFD8] accent-[#1A1916]" />
                Jadikan Admin
              </label>
              <button type="submit" disabled={inviting}
                className="w-full flex items-center justify-center gap-2 min-h-[44px] px-4 py-2.5 bg-[#1A1916] text-white rounded-xl text-sm font-body font-semibold hover:bg-[#2E2E2A] active:scale-[0.99] disabled:opacity-40 disabled:active:scale-100 transition-all">
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Invite
              </button>
            </div>
          </form>
          <p className="text-[11px] text-[#AEADA8] font-body mt-3">
            Sistem invite-only: user baru hanya bisa login setelah di-invite di sini.
          </p>
        </section>

        {/* ── Tabel user ───────────────────────────────────────────────────── */}
        <section className="bg-white border border-[#E8E7E3] rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F4F3F0] flex items-center gap-2">
            <Users2 className="w-4 h-4 text-[#6B6860]" />
            <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold">Daftar Anggota</h2>
            <span className="text-[10px] text-[#C0BDB6] font-body">{users.length} orang</span>
          </div>

          {loading ? (
            <div className="p-5 space-y-2">
              {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-[#F4F3F0] rounded-xl animate-pulse" />)}
            </div>
          ) : users.length === 0 ? (
            <div className="py-12 text-center">
              <Users2 className="w-7 h-7 text-[#C0BDB6] mx-auto mb-2" />
              <p className="text-sm text-[#8A8A85] font-body">Belum ada anggota</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-left">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#AEADA8] font-body border-b border-[#F4F3F0]">
                    <th className="px-5 py-2.5 font-semibold">Anggota</th>
                    <th className="px-3 py-2.5 font-semibold">Departemen</th>
                    <th className="px-3 py-2.5 font-semibold">Role</th>
                    <th className="px-3 py-2.5 font-semibold">Admin</th>
                    <th className="px-3 py-2.5 font-semibold" title="Grant: boleh mengatur siapa yang melihat item yang dia submit">Set Visibility</th>
                    <th className="px-3 py-2.5 font-semibold">Login Terakhir</th>
                    <th className="px-5 py-2.5 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F8F7F5]">
                  {users.map((u) => {
                    const isSelf = u.email === myEmail
                    const busy = busyEmail === u.email
                    // Pilihan role mengikuti departemen baris (Director hanya di HOLDING);
                    // role tak dikenal / tak sah (data lama) tetap tampil agar tidak hilang.
                    const allowed = rolesForDepartment(u.department) as readonly string[]
                    const opts = allowed.includes(u.role) ? [...allowed] : [u.role, ...allowed]
                    return (
                      <tr key={u.email} className="hover:bg-[#FAFAF8] transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-[#1A1916] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {(u.username || u.email).charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[13px] font-body font-semibold text-[#1A1916] truncate">
                                {u.username}{isSelf && <span className="ml-1.5 text-[10px] font-normal text-[#AEADA8]">(Anda)</span>}
                              </p>
                              <p className="text-[11px] text-[#AEADA8] font-body truncate">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-[#AEADA8] flex-shrink-0" />
                            <select
                              value={u.department ?? ''}
                              onChange={(e) => {
                                const next = e.target.value
                                setKonfirmasi({
                                  title: 'Pindah unit bisnis',
                                  message: `${u.username} dipindahkan dari ${u.department ?? 'tanpa departemen'} ke ${next}. Akses datanya ikut pindah unit bisnis.`,
                                  confirmLabel: 'Pindahkan',
                                  run: () => { void patchUser(u.email, { department: next }) },
                                })
                              }}
                              disabled={busy}
                              aria-label={`Departemen ${u.username}`}
                              className="min-h-[36px] px-2 py-1.5 rounded-lg text-[12px] font-body bg-white border border-[#E0DFD8] text-[#1A1916] focus:outline-none focus:border-[#6366F1] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
                              {!u.department && <option value="">—</option>}
                              {u.department && !(DEPARTMENTS as readonly string[]).includes(u.department) && (
                                <option value={u.department}>{u.department}</option>
                              )}
                              {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                            </select>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <select
                              value={u.role}
                              onChange={(e) => {
                                const next = e.target.value
                                setKonfirmasi({
                                  title: 'Ubah role',
                                  message: `Role ${u.username} diubah dari ${u.role} menjadi ${next}.`,
                                  confirmLabel: 'Ubah',
                                  run: () => { void patchUser(u.email, { role: next }) },
                                })
                              }}
                              disabled={busy}
                              aria-label={`Role ${u.username}`}
                              className={`min-h-[36px] px-2.5 py-1.5 rounded-lg text-[12px] font-body focus:outline-none focus:border-[#6366F1] cursor-pointer border ${
                                roleLevel(u.role) >= 2
                                  ? 'bg-[#EEF2FF] border-[#C7D2FE] text-[#4338CA] font-semibold'
                                  : 'bg-white border-[#E0DFD8] text-[#1A1916]'
                              } disabled:opacity-60 disabled:cursor-not-allowed`}>
                              {opts.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#AEADA8]" />}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" title={u.isAdmin ? 'Cabut flag admin' : 'Jadikan admin'}>
                            <input
                              type="checkbox"
                              checked={u.isAdmin}
                              onChange={(e) => {
                                const next = e.target.checked
                                setKonfirmasi({
                                  title: next ? 'Jadikan admin' : 'Cabut akses admin',
                                  message: next
                                    ? `${u.username} menjadi admin, dan bisa mengelola semua user di semua unit bisnis.`
                                    : `Akses admin ${u.username} dicabut.`,
                                  confirmLabel: next ? 'Jadikan admin' : 'Cabut',
                                  danger: !next,
                                  run: () => { void patchUser(u.email, { isAdmin: next }) },
                                })
                              }}
                              disabled={busy}
                              aria-label={`Flag admin ${u.username}`}
                              className="w-4 h-4 rounded border-[#E0DFD8] accent-[#1A1916] disabled:opacity-60"
                            />
                            {u.isAdmin && <ShieldCheck className="w-3.5 h-3.5 text-[#4338CA]" />}
                          </label>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              const next = !u.canSetVisibility
                              setKonfirmasi({
                                title: next ? 'Beri izin set visibility' : 'Cabut izin set visibility',
                                message: next
                                  ? `${u.username} boleh mengatur visibility item yang dia submit.`
                                  : `Izin set visibility ${u.username} dicabut.`,
                                confirmLabel: next ? 'Beri izin' : 'Cabut',
                                danger: !next,
                                run: () => { void patchUser(u.email, { canSetVisibility: next }) },
                              })
                            }}
                            disabled={busy}
                            aria-label={`Grant visibility ${u.username}`}
                            aria-pressed={u.canSetVisibility}
                            title="Grant manual: boleh mengatur visibility item yang dia submit (default hanya HR & Finance)"
                            className={`inline-flex items-center gap-1.5 min-h-[28px] px-2.5 rounded-full text-[11px] font-body font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                              u.canSetVisibility
                                ? 'bg-[#EEF2FF] border-[#C7D2FE] text-[#4338CA]'
                                : 'bg-white border-[#E0DFD8] text-[#AEADA8] hover:text-[#6B6860] hover:border-[#C0BDB6]'
                            }`}>
                            {u.canSetVisibility ? <><Eye className="w-3.5 h-3.5" /> Ya</> : 'Tidak'}
                          </button>
                        </td>
                        <td className="px-3 py-3">
                          <span className="text-[12px] font-body text-[#8A8A85]">
                            {u.lastLoginAt ? formatDate(u.lastLoginAt) : '—'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => askRemoveUser(u.email)}
                            disabled={busy || isSelf}
                            title={isSelf ? 'Tidak bisa menghapus akun sendiri' : 'Hapus user'}
                            aria-label={`Hapus ${u.username}`}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-[#C0BDB6] hover:text-[#DC2626] hover:bg-[#FEF2F2] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#C0BDB6] disabled:cursor-not-allowed transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <ConfirmDialog
        open={konfirmasi !== null}
        title={konfirmasi?.title ?? ''}
        message={konfirmasi?.message ?? ''}
        confirmLabel={konfirmasi?.confirmLabel}
        danger={konfirmasi?.danger}
        onConfirm={() => { const aksi = konfirmasi; setKonfirmasi(null); aksi?.run() }}
        onCancel={() => setKonfirmasi(null)}
      />
    </div>
  )
}
